/**
 * Terms, Privacy, Refunds and the responsible-play notice.
 *
 * Two rules held throughout, and they are the only reason these are worth reading:
 *
 * 1. **Every factual claim is true of the code as it stands.** The privacy policy
 *    lists the tables we actually write, the export and deletion described are the
 *    ones `/api/me` actually implements, and where a capability exists in the
 *    infrastructure but is not used — the R2 bucket, Sentry — it is not claimed. A
 *    policy that describes an aspirational product is worse than none, because it is
 *    a promise nobody is keeping.
 * 2. **Nothing here is a gambling product.** No stake, no prize, no payout. That is
 *    a licensing question in most of the world and the answer has to be unambiguous.
 *
 * The values in `LEGAL` are the ones only Tim can supply. Anything still in square
 * brackets renders a warning banner at the top of every legal page, because a
 * template that ships with `[STATE]` in it is worse than embarrassing — it tells a
 * reader the document was never actually adopted.
 */
import { Link, useLocation } from "react-router-dom";

const LEGAL = {
  /** Who the user is contracting with. No entity exists yet; Stripe is set up as an individual. */
  entity: "[LEGAL ENTITY OR THE TWO FOUNDERS BY NAME]",
  jurisdiction: "[STATE], United States",
  address: "[POSTAL ADDRESS]",
  contact: "support@bluffbusters.net",
  effective: "[EFFECTIVE DATE]",
} as const;

const UNFILLED = Object.entries(LEGAL).filter(([, v]) => v.includes("["));

/* ------------------------------------------------------------------ shell --- */

const PAGES = [
  { to: "/terms", label: "Terms" },
  { to: "/privacy", label: "Privacy" },
  { to: "/refunds", label: "Refunds" },
  { to: "/responsible-play", label: "Responsible play" },
] as const;

function LegalPage({ title, updated, children }: {
  title: string; updated?: string; children: React.ReactNode;
}) {
  const { pathname } = useLocation();
  return (
    <div className="page prose legal">
      {UNFILLED.length > 0 && (
        <div className="legal-todo" role="alert">
          <strong>Not finished.</strong> {UNFILLED.length} value
          {UNFILLED.length === 1 ? "" : "s"} in <code>web/src/routes/Legal.tsx</code>{" "}
          still need filling in: {UNFILLED.map(([k]) => k).join(", ")}. This banner is
          visible to everyone who opens this page, and it disappears when they are set.
        </div>
      )}

      <nav className="legal-nav">
        {PAGES.map((p) => (
          <Link key={p.to} to={p.to} className={pathname === p.to ? "on" : undefined}>
            {p.label}
          </Link>
        ))}
      </nav>

      <h1>{title}</h1>
      <p className="muted small">Effective {updated ?? LEGAL.effective}.</p>
      {children}

      <p className="muted small legal-foot">
        Questions about any of this: <a href={`mailto:${LEGAL.contact}`}>{LEGAL.contact}</a>.
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------ terms --- */

export function Terms() {
  return (
    <LegalPage title="Terms of Service">
      <p>
        These terms are an agreement between you and {LEGAL.entity} ("we", "us"), who
        operate BluffBusters at bluffbusters.net. By making an account you accept them.
        If you do not, do not make an account.
      </p>

      <h2>1. What BluffBusters is</h2>
      <p>
        BluffBusters is <strong>poker training software</strong>. It deals hands, lets
        you play them against computer opponents, and tells you what our model thinks
        of your decisions.
      </p>
      <p>
        <strong>There is no gambling here.</strong> You cannot deposit money, wager
        anything, win anything, or withdraw anything. The chips on the table are a
        display of a number and have no value of any kind — not real, not redeemable,
        not transferable, not tradeable. Your subscription pays for access to the
        software; it does not buy chips and it does not buy a chance at anything.
      </p>

      <h2>2. Your account</h2>
      <p>
        You must be 18 or older. One account per person, and you are responsible for
        what happens under yours. Sign-in is handled by Clerk; keep your credentials
        to yourself. Tell us at {LEGAL.contact} if you think someone else is using
        your account.
      </p>
      <p>
        We may suspend or close an account that is being used to break these terms, to
        attack the service, or to resell what is inside it. Where we can, we will tell
        you why first.
      </p>

      <h2>3. Subscriptions, trials and payment</h2>
      <p>
        A free tier exists and is genuinely free. Pro is a paid subscription, billed
        monthly or annually through <strong>Stripe</strong>, who handle the payment and
        store the card. We never see your card number.
      </p>
      <p>
        A Pro trial requires a card and runs for the period stated at checkout. Unless
        you cancel before it ends, it converts to a paid subscription at the price
        shown. One trial per account, ever. Subscriptions renew automatically until
        cancelled, and you can cancel at any time from your account page.
      </p>
      <p>
        We can change prices. If we do, the new price applies from your next renewal
        and we will tell you before it takes effect. Founder pricing, where offered,
        is honoured for as long as that subscription stays continuously active — a
        cancellation ends it.
      </p>
      <p>
        Some accounts are given free access as a gift. A gift confers no purchase, no
        contract of sale, and no consumer rights that follow from paying; we may end
        it at any time, though we do not expect to.
      </p>

      <h2>4. What we claim about the content, and what we do not</h2>
      <p>
        This matters more here than in most software, so it is in the terms rather than
        the small print.
      </p>
      <ul>
        <li>
          The preflop charts sold on the Pro tier come from a solve we computed
          ourselves. Where a combination could not be solved exactly, it is not sold —
          it is withheld and marked as such.
        </li>
        <li>
          <strong>The bots are not a solver.</strong> They play a heuristic model, and
          the cost shown against a decision you made is that same model's estimate.
          Nothing in the game is claimed to be game-theory optimal.
        </li>
        <li>
          <strong>Nothing here is a prediction that you will win money.</strong> Poker
          is a game of skill played for stakes we have nothing to do with. Training
          software cannot promise a result, and we do not.
        </li>
      </ul>

      <h2>5. What you may and may not do with it</h2>
      <p>
        Your subscription is a personal, non-transferable licence to use the software
        while it is active. You may not resell, redistribute, or republish the charts
        or solved data; scrape or bulk-extract them; share your account; or use the
        service to build a competing product. Automated access outside the normal
        interface is not permitted, and the service is rate-limited accordingly.
      </p>
      <p>
        Everything in the software — the code, the charts, the models, the words — is
        ours or licensed to us. Hands you play and settings you save are yours.
      </p>

      <h2>6. Availability</h2>
      <p>
        We will try to keep it running and we do not promise that we will. It is
        provided "as is", without warranty of any kind. To the fullest extent the law
        allows, our total liability to you is limited to what you paid us in the twelve
        months before the claim, and we are not liable for indirect or consequential
        loss — including, to be explicit, any money you lose playing poker anywhere
        else.
      </p>
      <p>
        Nothing in these terms limits liability that cannot lawfully be limited,
        including for death or personal injury caused by negligence, or for fraud.
      </p>

      <h2>7. Changes and endings</h2>
      <p>
        We may change these terms. Material changes will be notified by email or in the
        product before they take effect, and continuing to use the service after that
        means you accept them. You can close your account at any time from the account
        page; deletion is described in the <Link to="/privacy">privacy policy</Link>.
      </p>

      <h2>8. Law</h2>
      <p>
        These terms are governed by the laws of {LEGAL.jurisdiction}, and its courts
        have exclusive jurisdiction — except that if you are a consumer resident
        elsewhere, you keep the rights and protections of your own country's law.
      </p>
      <p className="muted small">
        {LEGAL.entity}, {LEGAL.address}.
      </p>
    </LegalPage>
  );
}

/* ---------------------------------------------------------------- privacy --- */

export function Privacy() {
  return (
    <LegalPage title="Privacy Policy">
      <p>
        This describes what we actually store, why, and how to get it back or get rid
        of it. It is written against the code, not against a template.
      </p>

      <h2>What we hold</h2>
      <table className="legal-table">
        <thead><tr><th>What</th><th>Why</th></tr></thead>
        <tbody>
          <tr>
            <td>Email address, and an account id from Clerk</td>
            <td>To have an account at all, and to email you about it</td>
          </tr>
          <tr>
            <td>Where you mostly play, and your stakes — if you answered</td>
            <td>Asked once at signup, optional, editable, used to pitch the coaching at the right level</td>
          </tr>
          <tr>
            <td>Your plan, its status, and a Stripe customer id</td>
            <td>To know what you have access to</td>
          </tr>
          <tr>
            <td>Settings you save</td>
            <td>So the product looks the same next time</td>
          </tr>
          <tr>
            <td>Training decisions and game sessions — <em>only with your consent</em></td>
            <td>To build your leak report</td>
          </tr>
          <tr>
            <td>Daily counts of spots used</td>
            <td>To enforce the free tier's limit. Counts only, no content</td>
          </tr>
        </tbody>
      </table>

      <h2>What we do not hold</h2>
      <ul>
        <li>
          <strong>Your card.</strong> Payment happens on Stripe's systems. We store a
          customer id and nothing else about your payment method.
        </li>
        <li>
          <strong>Your password.</strong> Authentication is Clerk's. We never see it.
        </li>
        <li>
          <strong>Hand-history uploads.</strong> The feature does not exist yet. If it
          ever does, this page changes before it ships, not after.
        </li>
        <li>
          <strong>Advertising or cross-site tracking data.</strong> There are no
          advertising trackers in this product, and we do not sell or share personal
          information for advertising.
        </li>
      </ul>

      <h2>Consent, and what happens without it</h2>
      <p>
        Recording your decisions for analysis is <strong>off by default</strong>. Not
        off-by-default-in-the-copy — off in the database, for every account, until you
        turn it on from your account page.
      </p>
      <p>
        With it off, the trainer and the game work identically. The poker itself runs
        entirely in your browser, so a session you play with analytics off is never
        sent to us at all: it stays on your device and the leak report simply has less
        to work with. You can turn it off again at any time, which stops new recording;
        use deletion below to remove what was already recorded.
      </p>

      <h2>Who else sees it</h2>
      <p>
        Only the processors that make the product work: <strong>Clerk</strong> (sign-in),
        <strong> Stripe</strong> (payment), and <strong>Cloudflare</strong>, who host
        everything — the site, the API, and the database. Aggregate, non-identifying
        numbers may inform what we build. We do not sell personal information.
      </p>

      <h2>Where it lives, and for how long</h2>
      <p>
        On Cloudflare's network, which is global; by using the service you accept that
        your data is processed in the countries Cloudflare operates in, including the
        United States. We keep your account data while your account exists, and your
        recorded decisions while consent stands. Some records — an invoice, an audit
        entry — are kept longer where the law requires it.
      </p>

      <h2>Your rights</h2>
      <p>
        Depending on where you live, you may have rights to access, correct, delete,
        port, or restrict the use of your personal data, and to object to it. Two of
        those are buttons in the product rather than a request you have to make:
      </p>
      <ul>
        <li>
          <strong>Export.</strong> Your account page produces a machine-readable file of
          your account, entitlements, settings, usage, decisions, session history and
          statistics. Immediately, no ticket.
        </li>
        <li>
          <strong>Delete.</strong> Also on the account page. It removes your decisions,
          statistics, usage counts, settings, game sessions, training sessions and
          entitlements outright, and anonymises the account record itself — your email
          is replaced so nothing identifying remains. It cannot be undone.
        </li>
      </ul>
      <p>
        Deleting your BluffBusters account does not by itself delete your Clerk sign-in
        record or your Stripe customer record; write to {LEGAL.contact} and we will
        deal with those too. Cancel any subscription before deleting, or you will be
        billed for an account you can no longer reach.
      </p>
      <p>
        Anything else, including a complaint: {LEGAL.contact}. If you are in the UK or
        EU you may also complain to your data protection authority.
      </p>

      <h2>Cookies</h2>
      <p>
        Only what is needed to work: a session cookie from Clerk to keep you signed in,
        and Cloudflare's security cookies. No advertising cookies, and no third-party
        analytics that follow you off this site.
      </p>

      <h2>Children</h2>
      <p>
        The service is for adults. We do not knowingly collect data from anyone under
        18; if we learn we have, we delete it.
      </p>
    </LegalPage>
  );
}

/* ---------------------------------------------------------------- refunds --- */

export function Refunds() {
  return (
    <LegalPage title="Refund Policy">
      <p>
        Short version: the trial is there so you can find out before you pay, you can
        cancel at any time, and if the product did not do what we said it does, tell us
        and we will refund you.
      </p>

      <h2>The trial</h2>
      <p>
        Pro starts with a free trial. It requires a card, it does not charge you during
        the trial, and cancelling before it ends costs you nothing. The end date is
        shown at checkout and on your account page.
      </p>

      <h2>Cancelling</h2>
      <p>
        Any time, from your account page — it opens Stripe's billing portal. You keep
        Pro until the end of the period you have already paid for, and you are not
        billed again. There is nothing to cancel by email and no retention call.
      </p>

      <h2>Refunds</h2>
      <ul>
        <li>
          <strong>Monthly.</strong> Cancelling stops the next charge. We do not
          routinely refund a month already started, because you had the month.
        </li>
        <li>
          <strong>Annual.</strong> Within 14 days of the charge, ask and we refund it
          in full. After that we will refund the unused whole months if you ask.
        </li>
        <li>
          <strong>Charged by mistake</strong> — a trial you thought you had cancelled, a
          duplicate, a renewal you did not expect and have not used — we refund it. Ask.
        </li>
        <li>
          <strong>It did not work.</strong> If the product was broken, or a chart you
          paid for was not there, that is on us and we refund it. This is not a
          goodwill gesture; you paid for something specific.
        </li>
      </ul>
      <p>
        Refunds go back to the original payment method through Stripe, usually within
        five to ten business days. Ask at {LEGAL.contact} with the email on the
        account.
      </p>
      <p>
        Where local consumer law gives you a stronger right to a refund — a statutory
        cooling-off period, for example — that law wins over anything on this page.
      </p>

      <h2>Gifted accounts</h2>
      <p>
        Some accounts have free access given by us. Nothing was paid, so there is
        nothing to refund.
      </p>
    </LegalPage>
  );
}

/* ------------------------------------------------------- responsible play --- */

export function ResponsiblePlay() {
  return (
    <LegalPage title="Responsible play">
      <p>
        BluffBusters is training software. There is no money in it, nothing to deposit
        and nothing to win. But it is about poker, and most people who train are also
        playing somewhere that does involve money — so this page is here rather than
        not.
      </p>

      <h2>What we will not tell you</h2>
      <p>
        We will not tell you that training makes you a winning player, that a leak
        report predicts a result, or that any amount of study makes poker safe to play
        with money you need. Poker is a game of skill and it is still gambling when
        there are stakes on it. Most people who play for money lose it.
      </p>
      <p>
        You will not find loss-chasing mechanics here either: no streak pressure, no
        daily-login rewards, no "one more session" nudges. If we ever add something
        that behaves that way, it will be a mistake and we would like to be told.
      </p>

      <h2>Signs worth taking seriously</h2>
      <p>
        In yourself or someone you know: playing with money meant for something else;
        chasing losses; borrowing to play; hiding how much you play or lose; playing to
        escape rather than to compete; and finding it hard to stop when you meant to.
      </p>

      <h2>Where to get help</h2>
      <p>
        In the United States, the <strong>National Problem Gambling Helpline</strong> is{" "}
        <strong>1-800-522-4700</strong> (1-800-GAMBLER) — call or text, 24 hours,
        confidential. More at{" "}
        <a href="https://www.ncpgambling.org" target="_blank" rel="noreferrer noopener">
          ncpgambling.org
        </a>.
      </p>
      <p>
        In the UK, <a href="https://www.gamcare.org.uk" target="_blank" rel="noreferrer noopener">
        GamCare</a> runs the National Gambling Helpline on <strong>0808 8020 133</strong>,
        also free and 24 hours. Elsewhere, your national health service or gambling
        regulator will list a local service.
      </p>
      <p>
        If you want to stop playing for money, self-exclusion schemes exist in most
        places and they work better than willpower. They have nothing to do with us and
        we will not know you used one.
      </p>

      <h2>Taking a break from this</h2>
      <p>
        Cancel from your account page and keep access to the end of the period, or
        delete the account outright — both are on the account page, neither needs to go
        through us, and we will not email you to come back.
      </p>
    </LegalPage>
  );
}
